import {
  AUFGABEN_GROESSEN,
  GROESSE_LABELS,
  TASK_PRIORITIES,
  TASK_STATUS_LABELS,
  ZEITFENSTER,
  ZEITFENSTER_LABELS,
  type Aufgabengroesse,
  type TaskPriority,
  type TaskStatus,
  type Zeitfenster,
  zaehltAlsEntwickler,
} from '@netcon-work/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Sparkles } from 'lucide-react'
import { useRef, useState } from 'react'
import { aehnlicheFinden } from '../lib/aehnlichkeit'
import {
  kiVorlagenQuery,
  meilensteineQuery,
  poolsQuery,
  sitzungQuery,
  vorhabenQuery,
  workspacesQuery,
} from '../lib/queries'
import { trpc } from '../lib/trpc'
import { AufgabenVerweis } from './AufgabeKontext'
import { MarkdownFeld } from './MarkdownFeld'
import { hintergrundSchliesst } from './ui/hintergrund'
import { anhangHochladen, anhangVerweis } from '../lib/anhang'
import { Button } from './ui/primitives'
import {
  useAktiveWelt,
  useAufgabenDieserWelt,
  useErwaehnbarePersonen,
  usePersonenDieserWelt,
  usePersonenFuerAbnahme,
  weltWechseln,
} from '../lib/welt'
import { nebenBestandVerwerfen } from '../lib/mutationen'
import { PersonenOptionen } from './PersonenOptionen'

/**
 * Aufgabe in Work anlegen (Phase 4, AP 4.2 – erweitert am 18.08.2026).
 *
 * ## Was am 18.08.2026 dazukam und warum
 *
 * Nils: *„Für das Erstellen einer neuen Aufgabe fehlen noch jede Menge Daten, die bei den
 * bestehenden Aufgaben möglich sind (mögliche Leute, Zeitfenster usw.)."* Der Dialog kannte
 * fünf Felder, das Aufgabendetail zwölf – wer eine Aufgabe vollständig anlegen wollte, legte
 * sie an, suchte sie und öffnete sie. Jetzt steht hier dieselbe Planung wie dort.
 *
 * Dazu zwei Knöpfe, die es im Detail **nicht** gibt, weil sie nur beim Entstehen etwas nützen:
 *
 * - **KI-Vorlage** – eine unter Einstellungen gepflegte Anweisung auf den Entwurf anwenden.
 *   Sie schlägt vor, sie setzt nicht: übernommen wird Feld für Feld, per Häkchen.
 * - **Gibt es das schon?** – Ähnlichkeitssuche über den Bestand, der ohnehin im Client liegt.
 *   Kostet nichts, antwortet sofort, blockiert nichts (`lib/aehnlichkeit.ts`).
 *
 * ## Was hier weiterhin bewusst fehlt
 *
 * **Die Quelle ist kein Feld.** Eine Aufgabe, die in Work entsteht, gehört Work. Ließe man
 * sie wählen, könnte man sich eine scheinbar aus GitLab stammende Aufgabe anlegen – die es
 * dort nie gab, und die der Abgleich später löschen wollen würde. Der Server setzt `work`
 * fest.
 *
 * **Startstatus ist „Bereit", nicht „Eingang".** Wer hier bewusst eine Aufgabe anlegt, hat
 * sie schon durchdacht; „Eingang" ist für das, was ungefiltert hereinkommt (Phase 6).
 *
 * ## Der Bearbeiter ist neu – und eine Abweichung
 *
 * Hier stand: *„Kein Bearbeiter. Zugewiesen wird nicht beim Anlegen: Ziehen ist der
 * Normalfall (Kap. 4.3)."* Das bleibt richtig, und das Feld steht deshalb leer und ohne
 * Vorauswahl da. Aber die Ausnahme kostete zwei Schritte, und Nils hat sie am 18.08.2026
 * ausdrücklich hier gewollt ([E-119](../../../../docs/ENTSCHEIDUNGEN.md)). Wer jemanden
 * einträgt, sieht sofort, was das bedeutet: die Pool-Auswahl verschwindet.
 */
export function AufgabeAnlegen({
  schliessen,
  vorbelegt,
}: {
  schliessen: () => void
  /**
   * **Woraus heraus die Aufgabe entsteht** (21.08.2026,
   * [E-205](../../../../docs/ENTSCHEIDUNGEN.md)).
   *
   * Aus dem Vorhaben-Detail heraus ist das Vorhaben schon beantwortet – wer dort auf
   * „+ Aufgabe" drückt, hat es gerade offen. Die Auswahl bleibt trotzdem stehen und
   * bedienbar: eine Vorbelegung ist eine Annahme, keine Festlegung, und beim Anlegen fällt
   * am ehesten auf, dass es doch woandershin gehört.
   */
  vorbelegt?: { vorhabenId?: string; meilensteinId?: string }
}) {
  const { data: pools = [] } = useQuery(poolsQuery)
  /**
   * **Nur wer in dieser Arbeitswelt ist** (Z-07, 26.08.2026). Wer nicht Mitglied ist, sähe
   * die Aufgabe nie – angelegt wird in der aktiven Arbeitswelt, also gilt deren Team.
   */
  const { personen } = usePersonenDieserWelt()
  /**
   * **Abnehmen darf auch, wer nicht in der Welt ist** (16.09.2026, E-355) – die Welt zuerst,
   * dann die übrigen Erreichbaren; Begründung am Hook.
   */
  const abnahme = usePersonenFuerAbnahme()
  const { data: ich } = useQuery(sitzungQuery)
  /**
   * **Im persönlichen Workspace gibt es kein Gegenüber** (Nils, 26.08.2026). Er hat genau ein
   * Mitglied; „nimmt ab" und „kommen in Frage" hätten dort genau eine mögliche Antwort – und
   * ein Feld mit einer möglichen Antwort ist keine Wahl, sondern eine Attrappe.
   */
  const { welt, persoenlich } = useAktiveWelt()
  const { data: workspaces = [] } = useQuery(workspacesQuery)
  const mehrere = workspaces.length > 1
  const { data: vorhaben = [] } = useQuery(vorhabenQuery)
  const { data: meilensteine = [] } = useQuery(meilensteineQuery)
  /**
   * **Die Dublettenprüfung sucht in der Arbeitswelt, in der angelegt wird** (Z-07). Eine
   * Warnung „gibt es schon" über eine Aufgabe, die man nicht öffnen kann, wäre ein Hinweis
   * ins Leere.
   */
  const { aufgaben: bestand } = useAufgabenDieserWelt()
  /**
   * **Auch hier lässt sich jemand ansprechen** (21.09.2026,
   * [E-369](../../../../docs/ENTSCHEIDUNGEN.md#e-369)).
   *
   * Ein Kollege, über Nils: *„In neue Aufgabe, da geht das mit dem @ noch nicht."* Das Feld
   * unten ist dasselbe `MarkdownFeld` wie im Detail – es bekam nur nie die Liste, und ohne
   * Liste taucht beim `@` keine Auswahl auf. [E-366](../../../../docs/ENTSCHEIDUNGEN.md#e-366)
   * hatte das Detail ansprechbar gemacht und diese Stelle übersehen.
   */
  const erwaehnbar = useErwaehnbarePersonen()
  const queryClient = useQueryClient()

  const [titel, setTitel] = useState('')
  const [beschreibung, setBeschreibung] = useState('')
  const [prio, setPrio] = useState<TaskPriority | ''>('')
  const [poolIds, setPoolIds] = useState<string[]>([])
  const [faellig, setFaellig] = useState('')
  const [zeitfenster, setZeitfenster] = useState<Zeitfenster | ''>('')
  const [groesse, setGroesse] = useState<Aufgabengroesse | ''>('')
  const [vorhabenId, setVorhabenId] = useState(vorbelegt?.vorhabenId ?? '')
  const [meilensteinId, setMeilensteinId] = useState(vorbelegt?.meilensteinId ?? '')
  const [assigneeId, setAssigneeId] = useState('')
  /**
   * **Wer anlegt, nimmt ab – als Vorbelegung** (16.09.2026,
   * [E-355](../../../../docs/ENTSCHEIDUNGEN.md#e-355)). Nils: *„Aufgaben, die die Mitarbeiter
   * gestellt haben, sollen auch von denen abgenommen werden."*
   *
   * `null` heißt „noch nicht angefasst" – dann steht die eigene Person im Feld, sobald die
   * Sitzung da ist. Sichtbar und änderbar, keine stille Regel: wer „der des Vorhabens" oder
   * jemand anderen will, wählt es hier. Im persönlichen Workspace bleibt das Feld weg, und
   * dann geht auch nichts mit (`persoenlich` unten).
   */
  const [mitOwnerId, setMitOwnerId] = useState<string | null>(null)
  const abnehmer = mitOwnerId ?? ich?.id ?? ''
  const [wunschIds, setWunschIds] = useState<string[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  /**
   * **Bilder, die vor dem Anlegen eingefügt wurden** (09.09.2026,
   * [E-338](../../../../docs/ENTSCHEIDUNGEN.md#e-338)). Christian Hunhoff: *„Kopieren von
   * «Snipping Tool» in Beschreibungsfeld bei Neuanlage einer Aufgabe funktioniert nicht."*
   * Ein Anhang hängt an einer Aufgabe, und die gibt es hier noch nicht. Deshalb werden die
   * Dateien zwischengehalten und **nach** dem Anlegen hochgeladen; ihre Verweise kommen dann
   * unter die Beschreibung. `angelegt` merkt sich die neue Kennung, damit ein zweiter Versuch
   * nach einem gescheiterten Hochladen nicht noch eine Aufgabe anlegt.
   */
  const [dateien, setDateien] = useState<File[]>([])
  const angelegt = useRef<string | null>(null)

  /**
   * **Die Welt wechseln, ohne den Dialog zu verlassen** (Z-07.10).
   *
   * Geleert wird alles, was auf eine Zeile der alten Welt zeigt – Pool, Vorhaben, Meilenstein,
   * Bearbeiter, Mit-Owner, Wünsche. Stehen bliebe sonst eine Kennung, die es hier nicht gibt:
   * die Auswahlliste zeigte dann leer an, und beim Absenden käme ein Fremdschlüsselfehler
   * statt einer Aufgabe.
   */
  function weltUmstellen(id: string) {
    if (!id || id === welt?.id) return
    setPoolIds([])
    setVorhabenId('')
    setMeilensteinId('')
    setAssigneeId('')
    setMitOwnerId(null)
    setWunschIds([])
    setFehler(null)
    weltWechseln(id, queryClient)
  }

  const aktive = personen.filter((p) => p.isActive)
  /**
   * „Kommen in Frage" und „Bearbeiter" fragen nach Entwickler:innen – wie im Detail, und
   * deshalb über denselben Begriff: `zaehltAlsEntwickler` nimmt das Häkchen „arbeitet mit"
   * mit ([E-199](../../../../docs/ENTSCHEIDUNGEN.md)). Hier stand bis zum 24.08.2026
   * `p.role === 'entwickler'`, und der Admin, der mitentwickelt, fehlte in beiden Listen –
   * die Sammeländerung vom 21.08. hatte diese Datei nicht erreicht (siehe unten, die NUL
   * im Entwurfsschlüssel macht sie für `grep` zur Binärdatei).
   */
  const entwickler = aktive.filter(zaehltAlsEntwickler)
  const meilensteineDesVorhabens = meilensteine.filter(
    (m) => m.vorhabenId === vorhabenId && !m.erreichtAm,
  )

  const anlegen = useMutation({
    mutationFn: async () => {
      const id =
        angelegt.current ??
        (
          await trpc.tasks.anlegen.mutate({
            title: titel.trim(),
            ...(beschreibung.trim() ? { description: beschreibung.trim() } : {}),
            ...(prio ? { priority: prio } : {}),
            ...(faellig ? { dueAt: new Date(faellig) } : {}),
            poolIds,
            zeitfenster: zeitfenster || null,
            groesse: groesse || null,
            mitOwnerPersonId: persoenlich ? null : abnehmer || null,
            wunschPersonIds: wunschIds,
            vorhabenId: vorhabenId || null,
            meilensteinId: meilensteinId || null,
            assigneeId: assigneeId || null,
          })
        ).id
      angelegt.current = id
      if (dateien.length > 0) {
        const verweise: string[] = []
        for (const datei of dateien) {
          verweise.push(anhangVerweis(await anhangHochladen({ aufgabeId: id }, datei)))
        }
        await trpc.tasks.beschreibungSetzen.mutate({
          id,
          text: [beschreibung.trim(), ...verweise].filter(Boolean).join('\n\n'),
        })
      }
      return id
    },
    onSuccess: () => {
      void nebenBestandVerwerfen(queryClient)
      void queryClient.invalidateQueries({ queryKey: ['pools'] })
      void queryClient.invalidateQueries({ queryKey: ['analytics'] })
      void queryClient.invalidateQueries({ queryKey: ['vorhaben'] })
      schliessen()
    },
    onError: (e: Error) =>
      setFehler(
        angelegt.current
          ? `Die Aufgabe ist angelegt, aber ein Bild kam nicht hinauf: ${e.message} – „Anlegen" versucht es erneut, ohne eine zweite Aufgabe anzulegen.`
          : e.message,
      ),
  })

  function absenden(e: React.FormEvent) {
    e.preventDefault()
    setFehler(null)
    if (titel.trim().length < 3) return setFehler('Der Titel braucht mindestens drei Zeichen.')
    anlegen.mutate()
  }

  /**
   * **Ein Vorschlag der KI wird übernommen, nicht eingesetzt.** Deshalb kommt er hier an,
   * nachdem der Mensch die Häkchen gesetzt hat – und nicht direkt aus der Antwort.
   */
  function vorschlagUebernehmen(werte: {
    titel?: string
    beschreibung?: string
    poolId?: string
    prioritaet?: TaskPriority
    groesse?: Aufgabengroesse
    zeitfenster?: Zeitfenster
  }) {
    if (werte.titel !== undefined) setTitel(werte.titel)
    if (werte.beschreibung !== undefined) setBeschreibung(werte.beschreibung)
    if (werte.poolId !== undefined) {
      setPoolIds((v) => (v.includes(werte.poolId as string) ? v : [...v, werte.poolId as string]))
    }
    if (werte.prioritaet !== undefined) setPrio(werte.prioritaet)
    if (werte.groesse !== undefined) setGroesse(werte.groesse)
    if (werte.zeitfenster !== undefined) setZeitfenster(werte.zeitfenster)
  }

  return (
    <div
      className="fixed inset-x-0 bottom-0 unter-leiste z-50 flex items-start justify-center bg-[rgb(23_22_20/0.38)] p-3 pt-[4vh] sm:p-6 sm:pt-[6vh]"
      /**
       * **Mit Text im Formular schließt der Hintergrund nicht** (09.09.2026,
       * [E-337](../../../../docs/ENTSCHEIDUNGEN.md#e-337)). Christian Hunhoff: *„dass der
       * ganze Text verschwindet, wenn versehentlich rausgeklickt ist nicht wirklich Ideal."*
       * Wer etwas getippt hat, geht über Abbrechen oder Escape – ein Klick daneben ist keine
       * Entscheidung, den Text wegzuwerfen.
       */
      {...hintergrundSchliesst(schliessen, titel.trim() === '' && beschreibung.trim() === '')}
      role="dialog"
      aria-modal="true"
      aria-label="Neue Aufgabe"
    >
      <form
        onSubmit={absenden}
        className="flex max-h-[min(90vh,100%)] sm:max-h-[min(86vh,100%)] w-full min-w-0 max-w-[620px] flex-col overflow-y-auto rounded-card border border-line bg-surface p-[18px] shadow-card"
      >
        {/**
         * **Wo diese Aufgabe landet, steht in der Überschrift** (Nils, 26.08.2026: *„man soll
         * da auch stärker sehen, in welchem Workspace das angelegt wird"*).
         *
         * Vorher hieß der Dialog „Neue Aufgabe in Work" – und in Work gibt es seit gestern
         * mehrere Arbeitswelten. Wer aus „Mein Tag" heraus anlegt, sieht den Umschalter nicht
         * einmal (die Ansicht ist ungebunden); ohne diese Zeile wäre der Zielort unsichtbar.
         *
         * Bei einem **persönlichen** Workspace steht der Zusatz „sieht niemand außer dir"
         * dabei: das ist die Zusage, auf die es dort ankommt, und der Moment, in dem man sie
         * lesen will, ist genau dieser.
         */}
        <h3 className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm font-bold">
          Neue Aufgabe in
          {/**
           * **Und man kann sie hier auch umstellen** (Z-07.10).
           *
           * Aus „Mein Tag" heraus ist das der Regelfall: die Ansicht ist ungebunden, man sieht
           * den Umschalter in der Leiste nicht, und die eigene Notiz gehört in die eigene Welt
           * und nicht in die, die man zuletzt angesehen hat.
           *
           * ## Warum die Wahl die **ganze** Ansicht mitschaltet
           *
           * Weil sonst zwei Wahrheiten im selben Fenster stünden. Pools, Vorhaben und die Liste
           * der Menschen kommen vom Server und hängen an der Kopfzeile `x-workspace`; nur das
           * Zielfeld umzustellen hieße, Pools aus Welt A an eine Aufgabe in Welt B zu heften –
           * der Server lehnte ab, und zwar erst nach dem Absenden.
           *
           * Die getippten Felder bleiben stehen (sie sind Zustand dieses Bauteils); die
           * ausgewählten Verweise werden geleert, weil sie auf Zeilen zeigen, die es in der
           * neuen Welt nicht gibt.
           */}
          {mehrere ? (
            <select
              value={welt?.id ?? ''}
              onChange={(e) => weltUmstellen(e.target.value)}
              aria-label="Arbeitswelt, in der die Aufgabe entsteht"
              className="rounded-[9px] border border-line2 bg-surface px-2 py-1 text-[12.5px] font-semibold text-accent-deep"
            >
              {workspaces.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.icon} {w.label}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-accent-deep">
              {welt?.icon} {welt?.label ?? 'Work'}
            </span>
          )}
          {persoenlich && (
            <span className="text-[11.5px] font-normal text-muted">· sieht niemand außer dir</span>
          )}
        </h3>

        <label className="mt-3 block text-[11.5px] font-semibold text-ink2">
          Titel
          <input
            value={titel}
            onChange={(e) => setTitel(e.target.value)}
            required
            autoFocus
            className="mt-1 w-full rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[13px] font-normal"
          />
        </label>

        <div className="mt-2.5 text-[11.5px] font-semibold text-ink2">
          Beschreibung <span className="font-normal text-muted">optional · Markdown</span>
          <div className="mt-1 font-normal">
            <MarkdownFeld
              wert={beschreibung}
              setzen={setBeschreibung}
              zeilen={4}
              erwaehnbar={erwaehnbar}
              platzhalter="Worum geht es? – @ erwähnt jemanden, Datei hierher ziehen oder einfügen …"
              beiDateien={(neue) => setDateien((d) => [...d, ...neue])}
            />
          </div>
          {dateien.length > 0 && (
            <ul
              className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11.5px] font-normal"
              aria-label="Wird nach dem Anlegen angehängt"
            >
              {dateien.map((d, i) => (
                <li
                  key={`${d.name}-${i}`}
                  className="flex items-center gap-1 rounded-md border border-line2 bg-page px-1.5 py-0.5"
                >
                  <span className="max-w-[220px] truncate" title={d.name}>
                    {d.type.startsWith('image/') ? '🖼 ' : '📎 '}
                    {d.name}
                  </span>
                  <button
                    type="button"
                    aria-label={`${d.name} entfernen`}
                    onClick={() => setDateien((alle) => alle.filter((_, j) => j !== i))}
                    className="cursor-pointer text-muted hover:text-pool-sofort"
                  >
                    ×
                  </button>
                </li>
              ))}
              <li className="text-muted">wird nach dem Anlegen angehängt</li>
            </ul>
          )}
        </div>

        {/**
         * **Die beiden Helfer stehen direkt unter dem Text**, den sie lesen – und über den
         * Feldern, die sie füllen. Oben neben der Überschrift wären sie Zierrat; hier sind
         * sie der nächste Handgriff.
         */}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <KIVorlagenKnopf
            titel={titel}
            beschreibung={beschreibung}
            pools={pools}
            uebernehmen={vorschlagUebernehmen}
            beiFehler={setFehler}
          />
          <DublettenKnopf titel={titel} beschreibung={beschreibung} bestand={bestand} />
        </div>

        <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          <label className="block text-[11.5px] font-semibold text-ink2">
            Priorität
            <select
              value={prio}
              onChange={(e) => setPrio(e.target.value as TaskPriority | '')}
              className="mt-1 w-full cursor-pointer rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[13px] font-normal"
            >
              <option value="">keine</option>
              {TASK_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-[11.5px] font-semibold text-ink2">
            Fällig <span className="font-normal text-muted">optional</span>
            <input
              type="date"
              value={faellig}
              onChange={(e) => setFaellig(e.target.value)}
              className="mt-1 w-full rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[13px] font-normal"
            />
          </label>

          <label className="block text-[11.5px] font-semibold text-ink2">
            Zeitfenster
            <select
              value={zeitfenster}
              onChange={(e) => setZeitfenster(e.target.value as Zeitfenster | '')}
              className="mt-1 w-full cursor-pointer rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[13px] font-normal"
            >
              <option value="">– nicht eingeordnet –</option>
              {ZEITFENSTER.map((z) => (
                <option key={z} value={z}>
                  {ZEITFENSTER_LABELS[z]}
                </option>
              ))}
            </select>
          </label>

          {/**
           * **Bei der Größe bleiben es Knöpfe** – wie im Detail: drei kurze Wörter passen in
           * eine Zeile, und dort ist das Klicken schneller als ein Auswahlfeld.
           */}
          <div className="text-[11.5px] font-semibold text-ink2">
            Größe <span className="font-normal text-muted">optional</span>
            <div className="mt-1 flex gap-1.5">
              {AUFGABEN_GROESSEN.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGroesse(g === groesse ? '' : g)}
                  aria-pressed={g === groesse}
                  className={`cursor-pointer rounded-lg px-2.5 py-[7px] text-xs font-semibold ${
                    g === groesse
                      ? 'bg-ink text-white'
                      : 'border border-line2 text-ink2 hover:bg-[#f3f2ef]'
                  }`}
                >
                  {GROESSE_LABELS[g]}
                </button>
              ))}
            </div>
          </div>

          <label className="block text-[11.5px] font-semibold text-ink2">
            Vorhaben <span className="font-normal text-muted">optional</span>
            <select
              value={vorhabenId}
              onChange={(e) => {
                setVorhabenId(e.target.value)
                /** Ein Meilenstein gehört zu **einem** Vorhaben – wechselt es, ist er hinfällig. */
                setMeilensteinId('')
              }}
              className="mt-1 w-full cursor-pointer rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[13px] font-normal"
            >
              <option value="">– keins –</option>
              {vorhaben
                .filter((v) => v.aktiv)
                .map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.label}
                  </option>
                ))}
            </select>
          </label>

          <label className="block text-[11.5px] font-semibold text-ink2">
            Meilenstein
            <select
              value={meilensteinId}
              disabled={!vorhabenId || meilensteineDesVorhabens.length === 0}
              onChange={(e) => setMeilensteinId(e.target.value)}
              className="mt-1 w-full cursor-pointer rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[13px] font-normal disabled:cursor-default disabled:text-muted"
            >
              <option value="">
                {!vorhabenId
                  ? '– erst ein Vorhaben –'
                  : meilensteineDesVorhabens.length === 0
                    ? '– keine offenen –'
                    : '– keiner –'}
              </option>
              {meilensteineDesVorhabens.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-[11.5px] font-semibold text-ink2">
            Bearbeiter <span className="font-normal text-muted">die Ausnahme</span>
            <select
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              className="mt-1 w-full cursor-pointer rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[13px] font-normal"
            >
              <option value="">– niemand, wird gezogen –</option>
              {entwickler.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.displayName}
                </option>
              ))}
            </select>
          </label>

          {!persoenlich && (
            <label className="block text-[11.5px] font-semibold text-ink2">
              Nimmt ab <span className="font-normal text-muted">Mit-Owner</span>
              <select
                value={abnehmer}
                onChange={(e) => setMitOwnerId(e.target.value)}
                className="mt-1 w-full cursor-pointer rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[13px] font-normal"
              >
                <option value="">– der des Vorhabens –</option>
                <PersonenOptionen inWelt={abnahme.inWelt} weitere={abnahme.weitere} />
              </select>
            </label>
          )}
        </div>

        {/**
         * **„Kommen in Frage" ist keine Zuweisung** (E-87): es ist die Antwort auf *„wer käme
         * dafür in Frage?"*. Beim Ziehen bekommen diese Leute den Vorrang; wer sie übergeht,
         * begründet es.
         */}
        {!persoenlich && (
          <fieldset className="mt-3">
            <legend className="text-[11.5px] font-semibold text-ink2">
              Kommen in Frage{' '}
              <span className="font-normal text-muted">Vorrang beim Ziehen, keine Zuweisung</span>
            </legend>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {entwickler.length === 0 && (
                <span className="text-[11.5px] text-muted">
                  Niemand ist als Entwickler:in gekennzeichnet – Rollen stehen in der
                  Benutzerverwaltung.
                </span>
              )}
              {entwickler.map((p) => {
                const an = wunschIds.includes(p.id)
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() =>
                      setWunschIds((v) => (an ? v.filter((id) => id !== p.id) : [...v, p.id]))
                    }
                    aria-pressed={an}
                    className={`cursor-pointer rounded-lg px-[11px] py-[5px] text-xs font-semibold ${
                      an
                        ? 'bg-accent text-white'
                        : 'border border-line2 text-ink2 hover:bg-[#f3f2ef]'
                    }`}
                  >
                    {p.displayName}
                  </button>
                )
              })}
            </div>
          </fieldset>
        )}

        {/**
         * **Mit Bearbeiter gibt es keinen Pool.** Dieselbe Regel wie beim Zuweisen: eine
         * Aufgabe, die jemand hat, liegt in keinem Vorrat mehr. Die Auswahl auszublenden ist
         * ehrlicher, als sie stehen zu lassen und serverseitig zu verwerfen.
         */}
        {assigneeId ? (
          <p className="mt-3 rounded-lg bg-[#f3f2ef] px-2.5 py-2 text-[11.5px] text-ink2">
            Mit Bearbeiter kein Pool: die Aufgabe ist vergeben und liegt in keinem Vorrat.
          </p>
        ) : (
          <fieldset className="mt-3">
            <legend className="text-[11.5px] font-semibold text-ink2">
              Pool <span className="font-normal text-muted">mehrere möglich</span>
            </legend>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {pools.map((pool) => {
                const an = poolIds.includes(pool.id)
                return (
                  <button
                    key={pool.id}
                    type="button"
                    onClick={() =>
                      setPoolIds((v) => (an ? v.filter((id) => id !== pool.id) : [...v, pool.id]))
                    }
                    aria-pressed={an}
                    className={`cursor-pointer rounded-lg px-[11px] py-[5px] text-xs font-semibold ${
                      an ? 'bg-ink text-white' : 'border border-line2 text-ink2 hover:bg-[#f3f2ef]'
                    }`}
                  >
                    {pool.icon} {pool.label}
                  </button>
                )
              })}
            </div>
            <p className="mt-1.5 text-[11px] text-muted">
              Ohne Pool liegt die Aufgabe in keinem Vorrat und kann nicht gezogen werden – sinnvoll
              nur, wenn sie gleich zugewiesen wird.
            </p>
          </fieldset>
        )}

        {fehler && (
          <p className="mt-2.5 rounded-lg bg-[#fdeaea] px-2.5 py-2 text-[12.5px] text-[#b02a2a]">
            {fehler}
          </p>
        )}

        <p className="mt-3 text-[11px] text-muted">
          Startet in „{TASK_STATUS_LABELS.bereit}". Ohne Bearbeiter: Ziehen ist der Normalfall,
          Zuweisen die Ausnahme (Kap. 4.3).
        </p>

        <div className="mt-3 flex gap-2">
          <Button type="submit" variant="primary" size="sm" disabled={anlegen.isPending}>
            {anlegen.isPending ? 'Legt an …' : 'Anlegen'}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={schliessen}>
            Abbrechen
          </Button>
        </div>
      </form>
    </div>
  )
}

/* ── Der KI-Knopf ─────────────────────────────────────────────────────── */

type VorschlagsFeld = 'titel' | 'beschreibung' | 'poolId' | 'prioritaet' | 'groesse' | 'zeitfenster'

type Vorschlag = Awaited<ReturnType<typeof trpc.ki.vorlageAnwenden.mutate>>

/**
 * **„KI" mit einem Auswahlmenü aus Vorlagen** (Nils, 18.08.2026).
 *
 * ## Warum die Vorlage gewählt wird und nicht ein Prompt eingetippt
 *
 * Weil die Anweisung die Arbeit ist. *„Formuliere das als Aufgabe für Entwickler, Kontext
 * zuerst, keine Höflichkeitsfloskeln"* schreibt niemand dreimal am Tag neu – und wer es doch
 * tut, schreibt es jedes Mal anders, und die Ergebnisse sind nicht vergleichbar. Die Vorlagen
 * stehen deshalb unter Einstellungen und hier nur zur Auswahl.
 *
 * ## Warum das Ergebnis nichts überschreibt
 *
 * Ein Knopf, der den eigenen Text ersetzt, ist beim ersten Fehlgriff ein verlorener Absatz –
 * und danach drückt ihn niemand mehr. Der Vorschlag steht deshalb **neben** dem Entwurf, Feld
 * für Feld abhakbar, und was nicht angehakt ist, bleibt wie es war.
 */
function KIVorlagenKnopf({
  titel,
  beschreibung,
  pools,
  uebernehmen,
  beiFehler,
}: {
  titel: string
  beschreibung: string
  pools: { id: string; label: string; icon: string | null }[]
  uebernehmen: (werte: {
    titel?: string
    beschreibung?: string
    poolId?: string
    prioritaet?: TaskPriority
    groesse?: Aufgabengroesse
    zeitfenster?: Zeitfenster
  }) => void
  beiFehler: (text: string) => void
}) {
  const { data: vorlagen = [], error } = useQuery(kiVorlagenQuery)
  const [menueOffen, setMenueOffen] = useState(false)
  const [vorschlag, setVorschlag] = useState<{ vorlageId: string; daten: Vorschlag } | null>(null)
  const [gewaehlt, setGewaehlt] = useState<Set<VorschlagsFeld>>(new Set())

  const anwenden = useMutation({
    mutationFn: (vorlageId: string) =>
      trpc.ki.vorlageAnwenden.mutate({ vorlageId, titel, beschreibung }),
    onSuccess: (daten, vorlageId) => {
      if (daten.fehler) return beiFehler(`KI: ${daten.fehler}`)
      const felder = new Set<VorschlagsFeld>()
      /** Alles, was das Modell gesagt hat, ist vorausgewählt – wer nichts will, verwirft. */
      for (const feld of [
        'titel',
        'beschreibung',
        'poolId',
        'prioritaet',
        'groesse',
        'zeitfenster',
      ] as const) {
        if (daten[feld]) felder.add(feld)
      }
      if (felder.size === 0) {
        return beiFehler('Die KI hatte nichts zu ändern – der Entwurf bleibt, wie er ist.')
      }
      setGewaehlt(felder)
      setVorschlag({ vorlageId, daten })
    },
    onError: (e: Error) => beiFehler(e.message),
  })

  /** Kein Zugang, keine Vorlagen – dann steht der Knopf gar nicht erst da. */
  if (error || vorlagen.length === 0) return null

  const umschalten = (feld: VorschlagsFeld) =>
    setGewaehlt((v) => {
      const neu = new Set(v)
      if (neu.has(feld)) neu.delete(feld)
      else neu.add(feld)
      return neu
    })

  function annehmen() {
    if (!vorschlag) return
    const d = vorschlag.daten
    uebernehmen({
      ...(gewaehlt.has('titel') && d.titel ? { titel: d.titel } : {}),
      ...(gewaehlt.has('beschreibung') && d.beschreibung ? { beschreibung: d.beschreibung } : {}),
      ...(gewaehlt.has('poolId') && d.poolId ? { poolId: d.poolId } : {}),
      ...(gewaehlt.has('prioritaet') && d.prioritaet ? { prioritaet: d.prioritaet } : {}),
      ...(gewaehlt.has('groesse') && d.groesse ? { groesse: d.groesse } : {}),
      ...(gewaehlt.has('zeitfenster') && d.zeitfenster ? { zeitfenster: d.zeitfenster } : {}),
    })
    /**
     * **Die Quote zählt die Übernahme, nicht den Aufruf.** Ohne diese Zeile stünde in der
     * Verwaltung, wie oft eine Vorlage benutzt wurde – und nicht, ob sie taugt. Bewusst ohne
     * `await`: eine Zählung darf niemanden warten lassen.
     */
    void trpc.ki.vorlageUebernommen.mutate({ id: vorschlag.vorlageId }).catch(() => undefined)
    setVorschlag(null)
  }

  return (
    <>
      <div className="relative">
        <button
          type="button"
          onClick={() => setMenueOffen((v) => !v)}
          disabled={anwenden.isPending}
          aria-expanded={menueOffen}
          className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-line2 px-2.5 py-[5px] text-xs font-semibold text-ink2 hover:bg-[#f3f2ef] disabled:cursor-default disabled:text-muted"
        >
          <Sparkles className="h-3.5 w-3.5" />
          {anwenden.isPending ? 'KI denkt nach …' : 'KI-Vorlage'}
        </button>

        {menueOffen && (
          <div className="absolute z-10 mt-1 w-[300px] rounded-lg border border-line bg-surface p-1 shadow-card">
            {vorlagen.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => {
                  setMenueOffen(false)
                  anwenden.mutate(v.id)
                }}
                className="block w-full cursor-pointer rounded-md px-2 py-1.5 text-left hover:bg-[#f3f2ef]"
              >
                <span className="block text-[12.5px] font-semibold">{v.name}</span>
                {v.hinweis && <span className="block text-[11px] text-muted">{v.hinweis}</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {vorschlag && (
        <div className="mt-1 w-full rounded-lg border border-accent bg-accent-soft/40 p-2.5">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[11.5px] font-bold text-accent-deep">Vorschlag der KI</span>
            <span className="text-[10.5px] text-muted">{vorschlag.daten.modell}</span>
          </div>
          {vorschlag.daten.begruendung && (
            <p className="mt-1 text-[11.5px] text-ink2">{vorschlag.daten.begruendung}</p>
          )}

          <div className="mt-1.5 flex flex-col gap-1">
            {vorschlag.daten.titel && (
              <VorschlagsZeile
                feld="titel"
                bezeichnung="Titel"
                wert={vorschlag.daten.titel}
                an={gewaehlt.has('titel')}
                umschalten={umschalten}
              />
            )}
            {vorschlag.daten.beschreibung && (
              <VorschlagsZeile
                feld="beschreibung"
                bezeichnung="Beschreibung"
                wert={vorschlag.daten.beschreibung}
                mehrzeilig
                an={gewaehlt.has('beschreibung')}
                umschalten={umschalten}
              />
            )}
            {vorschlag.daten.poolId && (
              <VorschlagsZeile
                feld="poolId"
                bezeichnung="Pool"
                wert={(() => {
                  const p = pools.find((x) => x.id === vorschlag.daten.poolId)
                  return p ? `${p.icon ?? ''} ${p.label}`.trim() : vorschlag.daten.poolId
                })()}
                an={gewaehlt.has('poolId')}
                umschalten={umschalten}
              />
            )}
            {vorschlag.daten.prioritaet && (
              <VorschlagsZeile
                feld="prioritaet"
                bezeichnung="Priorität"
                wert={vorschlag.daten.prioritaet}
                an={gewaehlt.has('prioritaet')}
                umschalten={umschalten}
              />
            )}
            {vorschlag.daten.groesse && (
              <VorschlagsZeile
                feld="groesse"
                bezeichnung="Größe"
                wert={GROESSE_LABELS[vorschlag.daten.groesse]}
                an={gewaehlt.has('groesse')}
                umschalten={umschalten}
              />
            )}
            {vorschlag.daten.zeitfenster && (
              <VorschlagsZeile
                feld="zeitfenster"
                bezeichnung="Zeitfenster"
                wert={ZEITFENSTER_LABELS[vorschlag.daten.zeitfenster]}
                an={gewaehlt.has('zeitfenster')}
                umschalten={umschalten}
              />
            )}
          </div>

          <div className="mt-2 flex gap-2">
            <Button
              type="button"
              variant="primary"
              size="sm"
              disabled={gewaehlt.size === 0}
              onClick={annehmen}
            >
              Angehaktes übernehmen
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setVorschlag(null)}>
              Verwerfen
            </Button>
          </div>
        </div>
      )}
    </>
  )
}

function VorschlagsZeile({
  feld,
  bezeichnung,
  wert,
  mehrzeilig = false,
  an,
  umschalten,
}: {
  feld: VorschlagsFeld
  bezeichnung: string
  wert: string
  mehrzeilig?: boolean
  an: boolean
  umschalten: (feld: VorschlagsFeld) => void
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2 rounded-md bg-surface px-2 py-1.5 text-[12px]">
      <input
        type="checkbox"
        checked={an}
        onChange={() => umschalten(feld)}
        className="mt-[3px] cursor-pointer"
      />
      <span className="w-[80px] shrink-0 text-[11px] font-semibold text-ink2">{bezeichnung}</span>
      <span className={mehrzeilig ? 'max-h-[140px] overflow-y-auto whitespace-pre-wrap' : ''}>
        {wert}
      </span>
    </label>
  )
}

/* ── „Gibt es das schon?" ─────────────────────────────────────────────── */

/**
 * **Die Dublettenprüfung** (Nils, 18.08.2026).
 *
 * Rechnet im Client über den Bestand, der ohnehin geladen ist – ohne Rundreise, ohne Kosten,
 * ohne Spinner (Leitplanke 1 und 2 in `CLAUDE.md`). Das Maß steht in `lib/aehnlichkeit.ts`.
 *
 * **Sie hält niemanden auf.** Zwei ähnliche Titel können zwei verschiedene Sachen sein –
 * „Fehler beim Export" gibt es in vier Bereichen. Deshalb ein Knopf, den man drückt, und
 * keine Sperre beim Anlegen.
 */
function DublettenKnopf({
  titel,
  beschreibung,
  bestand,
}: {
  titel: string
  beschreibung: string
  bestand: readonly {
    id: string
    title: string
    status: string
    assigneeName: string | null
    sourceLabel: string
  }[]
}) {
  const [ergebnis, setErgebnis] = useState<{
    fuer: string
    treffer: ReturnType<typeof aehnlicheFinden<(typeof bestand)[number]>>
  } | null>(null)

  /**
   * Der Entwurf, für den geprüft wurde – tippt jemand weiter, ist die Antwort veraltet.
   *
   * Das Trennzeichen zwischen Titel und Beschreibung stand bis zum 24.08.2026 als **echtes
   * Null-Byte** in der Datei. Damit galt sie für `file`, `grep` und alles, was darauf aufbaut,
   * als Binärdatei – und zwar lautlos: `grep -rn "role === 'entwickler'"` überging sie ohne
   * einen Hinweis. Genau so überlebte weiter oben die alte Rollenprüfung die Sammeländerung
   * vom 21.08. ([E-199](../../../../docs/ENTSCHEIDUNGEN.md)). Als Fluchtfolge geschrieben ist
   * der Wert derselbe und die Datei wieder auffindbar.
   */
  const jetzigerEntwurf = `${titel}\u0000${beschreibung}`
  const veraltet = ergebnis !== null && ergebnis.fuer !== jetzigerEntwurf

  return (
    <>
      <button
        type="button"
        disabled={titel.trim().length < 3}
        onClick={() =>
          setErgebnis({
            fuer: jetzigerEntwurf,
            treffer: aehnlicheFinden({ titel, beschreibung }, bestand),
          })
        }
        title={
          titel.trim().length < 3 ? 'Erst ein Titel – danach lässt sich vergleichen.' : undefined
        }
        className="cursor-pointer rounded-lg border border-line2 px-2.5 py-[5px] text-xs font-semibold text-ink2 hover:bg-[#f3f2ef] disabled:cursor-default disabled:text-muted"
      >
        Gibt es das schon?
      </button>

      {ergebnis && (
        <div className="mt-1 w-full rounded-lg border border-line px-2.5 py-2">
          {ergebnis.treffer.length === 0 ? (
            <p className="text-[11.5px] text-ink2">
              Nichts Ähnliches im Bestand gefunden
              {veraltet ? ' (Stand vor der letzten Änderung)' : ''}.
            </p>
          ) : (
            <>
              <p className="text-[11.5px] font-semibold text-ink2">
                {ergebnis.treffer.length} ähnliche{' '}
                {ergebnis.treffer.length === 1 ? 'Aufgabe' : 'Aufgaben'}
                {veraltet && (
                  <span className="font-normal text-muted"> · Stand vor dem Tippen</span>
                )}
              </p>
              <ul className="mt-1 flex flex-col gap-1">
                {ergebnis.treffer.map(({ eintrag, wert }) => (
                  <li key={eintrag.id} className="flex items-baseline gap-2 text-[12px]">
                    <span className="tabular w-[34px] shrink-0 text-right text-[11px] font-semibold text-muted">
                      {Math.round(wert * 100)}%
                    </span>
                    <AufgabenVerweis
                      aufgabeId={eintrag.id}
                      className="cursor-pointer text-left hover:underline"
                    >
                      {eintrag.title}
                    </AufgabenVerweis>
                    <span className="shrink-0 text-[11px] text-muted">
                      {TASK_STATUS_LABELS[eintrag.status as TaskStatus] ?? eintrag.status}
                      {eintrag.assigneeName ? ` · ${eintrag.assigneeName}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-1.5 text-[11px] text-muted">
                Ähnlich heißt nicht dasselbe – ein Blick hinein entscheidet.
              </p>
            </>
          )}
        </div>
      )}
    </>
  )
}
